import React from 'react';
import { bool, func, string } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import compose from 'lodash/fp/compose';
import { PHP_URL } from 'utils/constants';
import { withIntlProvider, lStorage } from 'modules/utils';

import withMobileDialog from '@material-ui/core/withMobileDialog';
import Image from 'common/Image';

// Assets
import CalendarAlt from 'images/CalendarAlt.svg';
import Clipboard from 'images/Clipboard.svg';
import LineGraphAlt from 'images/LineGraphAlt.svg';
import People from 'images/People.svg';

import { locales } from './locales';

import {
	SwitchCP4Dialog,
	DialogTitle,
	DialogContent,
	ConfirmButton,
	DismissButton,
	SectionTitle,
	SectionDescription,
} from './styles';

const propTypes = {
	fullScreen: bool,
	open: bool,
	onClose: func,
	username: string,
	navigateToOldCP: func,
};

const defaultProps = {};

const SwitchCP4Modal = ({
	fullScreen,
	open,
	onClose,
	navigateToOldCP,
	username,
}) => {
	const elementIdPrefix = 'switch-to-cp4-modal';

	// If the user dismisses the dialog, we will save the date in which this user performed such action.
	// On future logins we will check if a predefined time has passed to show it again (see CP3-2386).
	const onDismiss = () => {
		const lastSeen =
			lStorage.get({
				key: 'SWITCH_CP4_MODAL_LAST_DISMISSED',
				stringify: true,
			}) || {};
		lStorage.set({
			key: 'SWITCH_CP4_MODAL_LAST_DISMISSED',
			stringify: true,
			value: {
				...lastSeen,
				[username]: new Date().getTime(),
			},
		});

		navigateToOldCP();
	};

	// Delegate all switching logic to what's already implemented in old CP, so that we reduce complexity.
	const onPerformSwitch = () => {
		window.location.href = `${PHP_URL}/index.php?mode=switch_to_new_cp`;
	};

	return (
		<SwitchCP4Dialog fullScreen={fullScreen} open={open} onClose={onClose}>
			<DialogTitle>
				<h2 id={`${elementIdPrefix}-title`}>
					<FormattedMessage id="title" />
				</h2>
				<span id={`${elementIdPrefix}-subtitle`} className="subtitle">
					<FormattedMessage id="subtitle" />
				</span>
			</DialogTitle>
			<DialogContent>
				<div className="sections">
					<div id={`${elementIdPrefix}-dashboard`} className="section">
						<div className="icon">
							<Image className="icon" source={LineGraphAlt} altText="Graph" />
						</div>
						<div className="description">
							<SectionTitle>
								<FormattedMessage id="dashboard" />
							</SectionTitle>
							<SectionDescription className="sectionDescription">
								<FormattedMessage id="dashboardDesc" />
							</SectionDescription>
						</div>
					</div>
					<div id={`${elementIdPrefix}-workforce`} className="section">
						<div className="icon">
							<Image className="icon" source={People} altText="People" />
						</div>
						<div className="description">
							<SectionTitle>
								<FormattedMessage id="workforce" />
							</SectionTitle>
							<SectionDescription className="sectionDescription">
								<FormattedMessage id="workforceDesc" />
							</SectionDescription>
						</div>
					</div>
					<div id={`${elementIdPrefix}-end-of-day`} className="section">
						<div className="icon">
							<Image className="icon" source={CalendarAlt} altText="Calendar" />
						</div>
						<div className="description">
							<SectionTitle>
								<FormattedMessage id="endOfDay" />
							</SectionTitle>
							<SectionDescription className="sectionDescription">
								<FormattedMessage id="endOfDayDesc" />
							</SectionDescription>
						</div>
					</div>
					<div id={`${elementIdPrefix}-transactions`} className="section">
						<div className="icon">
							<Image className="icon" source={Clipboard} altText="Clipboard" />
						</div>
						<div className="description">
							<SectionTitle>
								<FormattedMessage id="transactions" />
							</SectionTitle>
							<SectionDescription className="sectionDescription">
								<FormattedMessage id="transactionsDesc" />
							</SectionDescription>
						</div>
					</div>
				</div>
				<div className="actionsContainer">
					<DismissButton
						id={`${elementIdPrefix}-see-later-button`}
						onClick={onDismiss}
					>
						<FormattedMessage id="seeLater" />
					</DismissButton>
					<ConfirmButton
						id={`${elementIdPrefix}-see-lavu-4-button`}
						onClick={onPerformSwitch}
					>
						<FormattedMessage id="seeLavu4" />
					</ConfirmButton>
				</div>
			</DialogContent>
		</SwitchCP4Dialog>
	);
};

SwitchCP4Modal.propTypes = propTypes;
SwitchCP4Modal.defaultProps = defaultProps;

export default compose(
	withIntlProvider(locales),
	withMobileDialog({ breakpoint: 'xs' })
)(SwitchCP4Modal);
